import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

interface User {
  id: string;
  email: string;
  profile: any;
  roles: string[];
  kyc: any;
  reputation: any;
  loyalty: any;
  wallet?: {
    balance: number;
    currency: string;
  };
  badges?: Array<{
    badgeId: string;
    name: string;
    description: string;
    awardedAt: string;
    awardedBy: string;
  }>;
  authentication?: {
    twoFactorEnabled?: boolean;
    passkeys?: Array<{ id: string; name: string; createdAt: string }>;
    accessHistory?: Array<{ timestamp: string; ip: string; userAgent: string }>;
  };
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  loginStep: number;
  authMethods: string[];
}

const initialState: AuthState = {
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: false,
  isLoading: false,
  error: null,
  loginStep: 1,
  authMethods: [],
};

// Async thunks
export const loginStep1 = createAsyncThunk(
  'auth/loginStep1',
  async (email: string) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login/step1`, { email });
    return response.data.data;
  }
);

export const loginStep2 = createAsyncThunk(
  'auth/loginStep2',
  async (credentials: any) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login/step2`, credentials);
    const { accessToken, refreshToken, user } = response.data.data;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    return { accessToken, refreshToken, user };
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: any) => {
    const response = await axios.post(`${API_BASE_URL}/auth/register`, userData);
    return response.data.data;
  }
);

export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { getState }) => {
    const state = getState() as { auth: AuthState };
    const token = state.auth.accessToken;
    
    if (!token) {
      throw new Error('No token');
    }

    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data.user;
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState }) => {
    const state = getState() as { auth: AuthState };
    const token = state.auth.accessToken;
    
    if (token) {
      try {
        await axios.post(
          `${API_BASE_URL}/auth/logout`,
          { refreshToken: state.auth.refreshToken },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (error) {
        // Continue with logout even if API call fails
      }
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData: any, { getState }) => {
    const state = getState() as { auth: AuthState };
    const token = state.auth.accessToken;
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await axios.put(
      `${API_BASE_URL}/auth/me`,
      profileData,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.data.user;
  }
);

export const sendPhoneVerificationCode = createAsyncThunk(
  'auth/sendPhoneVerificationCode',
  async (phoneNumber: string, { getState }) => {
    const state = getState() as { auth: AuthState };
    const token = state.auth.accessToken;
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await axios.post(
      `${API_BASE_URL}/auth/verify-phone/send`,
      { phoneNumber },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  }
);

export const verifyPhoneCode = createAsyncThunk(
  'auth/verifyPhoneCode',
  async ({ phoneNumber, code }: { phoneNumber: string; code: string }, { getState }) => {
    const state = getState() as { auth: AuthState };
    const token = state.auth.accessToken;
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await axios.post(
      `${API_BASE_URL}/auth/verify-phone/verify`,
      { phoneNumber, code },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.data.user;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoginStep: (state, action: PayloadAction<number>) => {
      state.loginStep = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login Step 1
      .addCase(loginStep1.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginStep1.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loginStep = 2;
        state.authMethods = action.payload.authMethods;
      })
      .addCase(loginStep1.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Login failed';
      })
      // Login Step 2
      .addCase(loginStep2.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginStep2.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.user = action.payload.user;
        state.loginStep = 1;
      })
      .addCase(loginStep2.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Login failed';
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Registration failed';
      })
      // Load User
      .addCase(loadUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(loadUser.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.loginStep = 1;
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update profile';
      })
      // Phone verification
      .addCase(sendPhoneVerificationCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendPhoneVerificationCode.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sendPhoneVerificationCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to send verification code';
      })
      .addCase(verifyPhoneCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyPhoneCode.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoading = false;
      })
      .addCase(verifyPhoneCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to verify phone number';
      });
  },
});

export const { setLoginStep, clearError } = authSlice.actions;
export default authSlice.reducer;

